import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { type FC, useRef, useState } from 'react'

import { LOGIN_OTP_STATE } from 'shared/consts'
import {
  ERROR_BADINPUT,
  ERROR_UNAVAILABLE,
  GENERAL_ERROR,
} from 'shared/consts/errorCodes'
import {
  EMAIL_FIELD,
  LOGIN_REGISTER_ACTION_FIELD,
  OTP_REQUEST_FORM_ACTION_FIELD_VALUE,
  TARGET_ANCHOR_FIELD,
  TARGET_FEATURE_CODE_FIELD,
  TARGET_PAGE_CODE_FIELD,
  TARGET_PAGE_COMMAND_FIELD,
  TARGET_PRODUCT_CODE_FIELD,
} from 'shared/consts/formFields'
import type { PageProperties } from 'types/pageProperties'
import IconArrowRight from 'views/components/atoms/Icon/IconArrowRight'
import Input from 'views/components/atoms/Input'
import InputTextWithWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'
import ErrorNotifications from 'views/components/organisms/ErrorNotifications'
import LoadingIndicatorButton from 'views/components/organisms/SellerRating/LoadingIndicatorButton'
import useMountPoints from 'views/enhancers/useMountPoints'
import {
  usePageProperties,
  usePublicConfig,
} from 'views/utils/shopAndRuntimeConfigUtils'

import './OneTimePasswordRequestForm.scss'

type ErrorToTranslationType = {
  [key: string]: JSX.Element
  [key: symbol]: JSX.Element
}

const errorToTranslation: ErrorToTranslationType = {
  [GENERAL_ERROR.toString()]: <Trans id="general.error" />,
  [ERROR_BADINPUT.toString()]: <Trans id="general.error" />,
  [ERROR_UNAVAILABLE.toString()]: <Trans id="login.error.unavailable" />,
}

type OneTimePasswordRequestFormProps = {
  isReRequest?: boolean
}

const OneTimePasswordRequestForm: FC<OneTimePasswordRequestFormProps> = ({
  isReRequest,
}) => {
  const { language } = usePublicConfig()
  const mountPoints = useMountPoints()
  const {
    email,
    isFriendlyCaptchaToggleActive,
    otpLoginErrors = [],
    targetPageParams,
  } = usePageProperties() as PageProperties & {
    otpLoginErrors: (string | symbol)[]
    isFriendlyCaptchaToggleActive: boolean
  }

  const [loading, setLoading] = useState(false)
  const otpFormReference = useRef<HTMLFormElement | null>(null)

  let targetPageCode, targetProductCode, targetAnchor, targetPageCommand

  if (targetPageParams) {
    targetPageCode = targetPageParams.targetPageCode
    targetProductCode = targetPageParams.targetProductCode
    targetAnchor = targetPageParams.targetAnchor
    targetPageCommand = targetPageParams.targetPageCommand
  }

  const translatedErrors =
    otpLoginErrors.length > 0
      ? otpLoginErrors.map(error => errorToTranslation[error])
      : []

  const handleSubmit = () => {
    setLoading(true)
    if (otpFormReference.current?.checkValidity() === false) {
      setLoading(false)
    }
  }

  return (
    <>
      <h2 className="o-OneTimePasswordRequestForm__title">
        {isReRequest ? (
          <Trans id="login.otp.reRequestCode" />
        ) : (
          <Trans id="login.otp.withCode" />
        )}
      </h2>
      {translatedErrors && translatedErrors.length > 0 && (
        <div className="u-margin-large--bottom">
          <ErrorNotifications errors={translatedErrors} />
        </div>
      )}
      <p className="o-OneTimePasswordRequestForm__paragraph">
        <Trans id="login.otp.willSendEmail" />
      </p>

      {isReRequest && (
        <p className="o-OneTimePasswordRequestForm__label u-margin-large--top">
          {i18n._('login.top.pleaseVerifyEmail')}
        </p>
      )}
      <form
        name="otp-request-form"
        data-clientside-hook="formValidation"
        action={mountPoints.getLogin(
          targetPageParams,
          LOGIN_OTP_STATE.OTP_REQUEST
        )}
        method="POST"
        ref={otpFormReference}
        onSubmit={handleSubmit}
      >
        <ul className="a-list-reset">
          <li className="u-margin--bottom o-LoginForm__email">
            <InputTextWithWithFloatingLabel
              name={EMAIL_FIELD}
              id={'otp-input-email-id'}
              inputType="email"
              required
              defaultValue={email}
            >
              <Trans id="general.label.email" />
            </InputTextWithWithFloatingLabel>
          </li>
        </ul>

        <Input type="hidden" name={TARGET_FEATURE_CODE_FIELD} value="" />
        {targetAnchor && (
          <Input
            type="hidden"
            name={TARGET_ANCHOR_FIELD}
            value={targetAnchor}
          />
        )}
        {targetProductCode && (
          <Input
            type="hidden"
            name={TARGET_PRODUCT_CODE_FIELD}
            value={targetProductCode}
          />
        )}
        {targetPageCode && (
          <Input
            type="hidden"
            name={TARGET_PAGE_CODE_FIELD}
            value={targetPageCode}
          />
        )}
        {targetPageCommand && (
          <Input
            type="hidden"
            name={TARGET_PAGE_COMMAND_FIELD}
            value={targetPageCommand}
          />
        )}

        <input
          type="hidden"
          name={LOGIN_REGISTER_ACTION_FIELD}
          value={OTP_REQUEST_FORM_ACTION_FIELD_VALUE}
        />
        <div className="u-margin-large--bottom">
          <LoadingIndicatorButton
            id="otp-request-submit-btn"
            name="otp-request-submit-button"
            loading={loading}
            data-loading-label={i18n._('login.otp.request')}
            data-qa-id="otp-request-submit-button"
            disabled={isFriendlyCaptchaToggleActive}
            className="o-OneTimePasswordRequestForm__submit-button"
          >
            {isReRequest ? (
              <Trans id="login.otp.reRequestCode" />
            ) : (
              <Trans id="login.otp.requestCode" />
            )}
            <IconArrowRight size="small" />
          </LoadingIndicatorButton>
        </div>
        <p className="u-margin-large--bottom">
          <Trans id="login.otp.verifyEmail" />
        </p>
        <div className="u-margin--top u-margin--bottom u-text-align--center">
          <a
            href={mountPoints.getLogin(
              targetPageParams,
              LOGIN_OTP_STATE.LOGIN_REGISTER
            )}
            className="o-OneTimePasswordRequestForm__link"
            data-qa-id="otp-back-to-login-with-password"
          >
            <Trans id="login.otp.backToLoginWithPassword" />
          </a>
        </div>
        {isFriendlyCaptchaToggleActive && (
          <div
            className="frc-captcha"
            id="otpRequestForm-frc-captcha"
            data-sitekey="FCMUL52LQ1I634TN"
            data-lang={language}
          />
        )}
      </form>
    </>
  )
}

export default OneTimePasswordRequestForm
